import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  TitleStrategy,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { routes } from './routing/app.routes';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { authenticationInterceptor } from './auth/interceptors/auth.interceptor';
import { CombinedTitleStrategy } from './routing/title-strategy.service';
import { LoadingService } from './shared/services/loading.service';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { cacheInterceptor, loadingInterceptor, responseInterceptor } from '@at/shared';
import { provideNativeDateAdapter } from '@angular/material/core';
import { FeaturesProvider } from '@at/shared';
import { MatIconRegistry } from '@angular/material/icon';
import { RuntimeConfigService } from './shared/services/runtime-config.service';
import { provideSessionUrlBlacklist } from './tokens/session-url-blacklist.token';

const initializeMaterialSymbols = (registry: MatIconRegistry) => () => {
  registry.setDefaultFontSetClass('material-symbols-outlined');
};

const provideMaterialSymbols = (): Provider => ({
  provide: APP_INITIALIZER,
  useFactory: initializeMaterialSymbols,
  multi: true,
  deps: [MatIconRegistry],
});

const runtimeConfigInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (configService: RuntimeConfigService) => () => configService.loadConfig(),
  deps: [RuntimeConfigService],
  multi: true,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withViewTransitions(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    { provide: TitleStrategy, useClass: CombinedTitleStrategy },
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    FeaturesProvider,
    // Reuse the prerendered (SSG) DOM on bootstrap instead of destroying and
    // re-rendering it — avoids the swap flicker on '/'. Routes served from the
    // CSR shell have no server DOM and bootstrap normally.
    provideClientHydration(),
    provideAnimations(),
    importProvidersFrom(LoadingService),
    provideHttpClient(
      withInterceptors([authenticationInterceptor, responseInterceptor, loadingInterceptor, cacheInterceptor]),
      withFetch(),
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    provideMaterialSymbols(),
    runtimeConfigInitializer,
    provideSessionUrlBlacklist([
      '/',
      '/creator',
      '/operator',
      '/traveller',
      '/about',
      '/privacy-policy',
      '/cookie-policy',
      '/terms-of-use',
      '/subprocessor',
      '/sign-in',
      '/sign-up',
      '/confirm',
      '/forgot-password',
      '/reset-password',
      '/trip/create',
    ]),
  ],
};
